import {
    useRef,
    useCallback,
    useEffect,
    useState,
    type RefObject,
} from 'react';

/** Minimum drag distance before a gesture is treated as a scroll (not a click). */
const DRAG_THRESHOLD_PX = 5;

/** Recent pointer samples used to estimate release velocity. */
const VELOCITY_SAMPLE_WINDOW_MS = 100;

/** Ignore samples with near-zero duration when estimating velocity. */
const MIN_VELOCITY_SAMPLE_DT_MS = 1;

/** Minimum release velocity (px/ms) required to start inertial scrolling. */
const MIN_MOMENTUM_VELOCITY_PX_PER_MS = 0.05;

/** Stop inertial scrolling once velocity falls below this threshold (px/ms). */
const MOMENTUM_STOP_VELOCITY_PX_PER_MS = 0.01;

/**
 * Exponential friction coefficient for time-based deceleration.
 * Higher values stop sooner; tuned for a short, natural desktop flick.
 */
const MOMENTUM_FRICTION_PER_MS = 0.004;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface VelocitySample {
    timeMs: number;
    clientX: number;
}

interface UseDragScrollOptions {
    /** Sensitivity multiplier for scroll speed (default: 1). */
    sensitivity?: number;
    /** Optional external ref to use instead of creating a new one. */
    externalRef?: RefObject<HTMLElement | null>;
    /**
     * When true, continue scrolling with decelerating inertia after a mouse flick.
     * Opt-in so other drag surfaces keep stop-on-release behaviour.
     */
    momentum?: boolean;
}

interface UseDragScrollReturn {
    /** Ref to attach to the scrollable container. */
    scrollRef: RefObject<HTMLElement | null>;
    /** Whether the user is currently dragging. */
    isDragging: boolean;
}

/**
 * Returns true when the user has requested reduced motion.
 */
const prefersReducedMotion = (): boolean => {
    if (
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
    ) {
        return false;
    }

    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

/**
 * Estimates horizontal pointer velocity (px/ms) from recent samples.
 */
const estimateVelocityPxPerMs = (samples: VelocitySample[]): number => {
    const first = samples[0];
    const last = samples[samples.length - 1];
    if (!first || !last || samples.length < 2) {
        return 0;
    }

    const dt = last.timeMs - first.timeMs;
    if (dt < MIN_VELOCITY_SAMPLE_DT_MS) {
        return 0;
    }

    return (last.clientX - first.clientX) / dt;
};

/**
 * Clamps scrollLeft to the element's scrollable range.
 */
const clampScrollLeft = (
    element: HTMLElement,
    nextScrollLeft: number,
): number => {
    const maxScrollLeft = Math.max(
        0,
        element.scrollWidth - element.clientWidth,
    );
    return Math.min(maxScrollLeft, Math.max(0, nextScrollLeft));
};

/**
 * Hook that enables horizontal mouse drag-to-scroll on a container element.
 * Optionally continues with decelerating inertia after release when `momentum` is enabled.
 * Touch scrolling stays native. Meaningful drags suppress the following click to avoid
 * accidental link navigation.
 *
 * @param options - Configuration options
 * @returns Object containing the ref to attach and dragging state
 */
export const useDragScroll = (
    options: UseDragScrollOptions = {},
): UseDragScrollReturn => {
    const { sensitivity = 1, externalRef, momentum = false } = options;
    const internalRef = useRef<HTMLElement>(null);
    const scrollRef = externalRef ?? internalRef;
    const isDraggingRef = useRef(false);
    const activePointerIdRef = useRef<number | null>(null);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const hasDraggedRef = useRef(false);
    const velocitySamplesRef = useRef<VelocitySample[]>([]);
    const momentumRafRef = useRef<number | null>(null);
    const momentumVelocityRef = useRef(0);
    const momentumLastTimeRef = useRef<number | null>(null);

    const clearDragStyles = useCallback((container: HTMLElement | null) => {
        if (!container) {
            return;
        }

        container.style.cursor = '';
        container.style.userSelect = '';
    }, []);

    const cancelMomentum = useCallback(() => {
        if (momentumRafRef.current !== null) {
            cancelAnimationFrame(momentumRafRef.current);
            momentumRafRef.current = null;
        }
        momentumVelocityRef.current = 0;
        momentumLastTimeRef.current = null;
    }, []);

    const pushVelocitySample = useCallback(
        (clientX: number, timeMs: number) => {
            const samples = velocitySamplesRef.current;
            samples.push({ clientX, timeMs });

            const windowStart = timeMs - VELOCITY_SAMPLE_WINDOW_MS;
            while (samples.length > 0) {
                const oldestSample = samples[0];
                if (!oldestSample || oldestSample.timeMs >= windowStart) {
                    break;
                }
                samples.shift();
            }
        },
        [],
    );

    const startMomentum = useCallback(
        (releaseVelocityPxPerMs: number) => {
            const container = scrollRef.current;
            if (!container || !momentum || prefersReducedMotion()) {
                return;
            }

            // Pointer velocity is opposite to scrollLeft direction.
            const scrollVelocity = -releaseVelocityPxPerMs * sensitivity;
            if (Math.abs(scrollVelocity) < MIN_MOMENTUM_VELOCITY_PX_PER_MS) {
                return;
            }

            cancelMomentum();
            momentumVelocityRef.current = scrollVelocity;
            momentumLastTimeRef.current = null;

            const animate = (timeMs: number) => {
                const element = scrollRef.current;
                if (!element) {
                    cancelMomentum();
                    return;
                }

                const lastTimeMs = momentumLastTimeRef.current;
                momentumLastTimeRef.current = timeMs;
                const dtMs =
                    lastTimeMs === null ? 16 : Math.max(0, timeMs - lastTimeMs);
                let velocity = momentumVelocityRef.current;

                if (dtMs > 0) {
                    const unclampedScrollLeft =
                        element.scrollLeft + velocity * dtMs;
                    const nextScrollLeft = clampScrollLeft(
                        element,
                        unclampedScrollLeft,
                    );
                    element.scrollLeft = nextScrollLeft;

                    const maxScrollLeft = Math.max(
                        0,
                        element.scrollWidth - element.clientWidth,
                    );
                    const hitStartBoundary =
                        unclampedScrollLeft < 0 && nextScrollLeft <= 0;
                    const hitEndBoundary =
                        unclampedScrollLeft > maxScrollLeft &&
                        nextScrollLeft >= maxScrollLeft;

                    if (hitStartBoundary || hitEndBoundary) {
                        cancelMomentum();
                        return;
                    }

                    velocity *= Math.exp(-MOMENTUM_FRICTION_PER_MS * dtMs);
                    momentumVelocityRef.current = velocity;
                }

                if (Math.abs(velocity) < MOMENTUM_STOP_VELOCITY_PX_PER_MS) {
                    cancelMomentum();
                    return;
                }

                momentumRafRef.current = requestAnimationFrame(animate);
            };

            momentumRafRef.current = requestAnimationFrame(animate);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cancelMomentum, momentum, sensitivity],
    );

    const endDrag = useCallback(
        (pointerId: number, shouldStartMomentum: boolean) => {
            const container = scrollRef.current;
            if (
                !isDraggingRef.current ||
                activePointerIdRef.current !== pointerId
            ) {
                return;
            }

            isDraggingRef.current = false;
            activePointerIdRef.current = null;
            clearDragStyles(container);

            if (container?.hasPointerCapture?.(pointerId)) {
                try {
                    container.releasePointerCapture(pointerId);
                } catch {
                    // Capture may already have been released by the browser.
                }
            }

            if (!shouldStartMomentum) {
                velocitySamplesRef.current = [];
                return;
            }

            const releaseVelocity = estimateVelocityPxPerMs(
                velocitySamplesRef.current,
            );
            velocitySamplesRef.current = [];
            startMomentum(releaseVelocity);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clearDragStyles, startMomentum],
    );

    const handlePointerDown = useCallback(
        (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) {
                return;
            }

            const container = scrollRef.current;
            if (!container) {
                return;
            }

            cancelMomentum();

            isDraggingRef.current = true;
            hasDraggedRef.current = false;
            activePointerIdRef.current = event.pointerId;
            startXRef.current = event.clientX;
            scrollLeftRef.current = container.scrollLeft;
            velocitySamplesRef.current = [];
            pushVelocitySample(event.clientX, performance.now());

            container.style.cursor = 'grabbing';
            container.style.userSelect = 'none';

            try {
                container.setPointerCapture(event.pointerId);
            } catch {
                // Some test environments do not implement pointer capture.
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cancelMomentum, pushVelocitySample],
    );

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (
                !isDraggingRef.current ||
                activePointerIdRef.current !== event.pointerId
            ) {
                return;
            }

            const container = scrollRef.current;
            if (!container) {
                return;
            }

            event.preventDefault();
            const walk = (event.clientX - startXRef.current) * sensitivity;

            if (Math.abs(walk) > DRAG_THRESHOLD_PX) {
                hasDraggedRef.current = true;
            }

            pushVelocitySample(event.clientX, performance.now());
            container.scrollLeft = scrollLeftRef.current - walk;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pushVelocitySample, sensitivity],
    );

    const handlePointerUp = useCallback(
        (event: PointerEvent) => {
            endDrag(event.pointerId, true);
        },
        [endDrag],
    );

    const handlePointerCancel = useCallback(
        (event: PointerEvent) => {
            endDrag(event.pointerId, false);
        },
        [endDrag],
    );

    const handleLostPointerCapture = useCallback(
        (event: PointerEvent) => {
            // Browser can revoke capture without a matching pointerup; end cleanly.
            endDrag(event.pointerId, true);
        },
        [endDrag],
    );

    /** Prevent native browser link-drag (drag-to-bookmark), which steals pointermove events. */
    const handleDragStart = useCallback((event: DragEvent) => {
        event.preventDefault();
    }, []);

    /** Suppress the click that browsers fire after a meaningful drag. */
    const handleClick = useCallback((event: MouseEvent) => {
        if (hasDraggedRef.current) {
            event.preventDefault();
            event.stopPropagation();
            hasDraggedRef.current = false;
        }
    }, []);

    // Sync the live DOM node into state so listeners rebind when the scroll
    // container remounts (e.g. ProductsFilterBar Products/Brands toggle).
    const [element, setElement] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setElement(scrollRef.current);
    });

    useEffect(() => {
        const container = element;
        if (!container) {
            return;
        }

        container.addEventListener('pointerdown', handlePointerDown);
        container.addEventListener('pointermove', handlePointerMove);
        container.addEventListener('pointerup', handlePointerUp);
        container.addEventListener('pointercancel', handlePointerCancel);
        container.addEventListener(
            'lostpointercapture',
            handleLostPointerCapture,
        );
        container.addEventListener('click', handleClick, true);
        container.addEventListener('dragstart', handleDragStart);

        return () => {
            container.removeEventListener('pointerdown', handlePointerDown);
            container.removeEventListener('pointermove', handlePointerMove);
            container.removeEventListener('pointerup', handlePointerUp);
            container.removeEventListener('pointercancel', handlePointerCancel);
            container.removeEventListener(
                'lostpointercapture',
                handleLostPointerCapture,
            );
            container.removeEventListener('click', handleClick, true);
            container.removeEventListener('dragstart', handleDragStart);
            clearDragStyles(container);
            cancelMomentum();
        };
    }, [
        element,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handlePointerCancel,
        handleLostPointerCapture,
        handleClick,
        handleDragStart,
        clearDragStyles,
        cancelMomentum,
    ]);

    return {
        scrollRef,
        isDragging: isDraggingRef.current,
    };
};
